import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setIDVerification, setIncomeVerification } from 'state/slices/idIncomeVerificationSlice';

import { useFetchVerificationStatus } from './useFetchVerificationStatus';
import { useFetchVerificationNotification } from './useTriggerNotification';
import { isVerificationCompleted } from './verificationHelpers';

interface CheckStatusState {
  showButton: boolean;
  countdown: number;
  isChecking: boolean;
  error: string | null;
}

/**
 * Custom hook for manual status checking after polling completes
 *
 * Features:
 * - Shows "Check Status" button when polling ends without desired result
 * - Fetches verification status on button click
 * - Implements 30-second countdown/cooldown after each check
 * - Updates Redux store when success/failed status is received
 * - Hides button automatically when desired status is found
 *
 * @param pollingCompleted - Whether polling has completed
 * @param lastPollingStatus - Last status from polling
 * @returns Button state, countdown value, and check function
 */
export const useCheckVerificationStatus = () => {
  const dispatch = useDispatch();
  const { fetchVerificationStatus } = useFetchVerificationStatus();
  const { fetchVerificationNotifications } = useFetchVerificationNotification();

  // State management
  const [state, setState] = useState<CheckStatusState>({
    showButton: false,
    countdown: 0,
    isChecking: false,
    error: null,
  });

  // Timer reference for countdown
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Configuration
  const COUNTDOWN_SECONDS = parseInt(
    process.env.ID_INCOME_VERIFICATION_COUNTDOWN_SECONDS ?? '30',
    10,
  ); // 30 seconds

  /**
   * Clear countdown timer
   */
  const clearCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  /**
   * Check if we have reached desired state for both verifications
   */
  const hasReachedDesiredState = useCallback((status: any): boolean => {
    const idStatus = status?.idVerification?.status;
    const incomeStatus = status?.incomeVerification?.status;

    // If both are null, not in desired state
    if (idStatus === null && incomeStatus === null) {
      return false;
    }

    // Check if at least one has a final status
    const idIsDesired = idStatus === null || isVerificationCompleted(idStatus);
    const incomeIsDesired = incomeStatus === null || isVerificationCompleted(incomeStatus);

    return idIsDesired && incomeIsDesired;
  }, []);

  const checkNotifications = useCallback(
    async (status: any) => {
      const checkId = isVerificationCompleted(status?.idVerification?.status);
      const checkIncome = isVerificationCompleted(status?.incomeVerification?.status);
      await fetchVerificationNotifications(checkId, checkIncome);
    },
    [fetchVerificationNotifications],
  );

  /**
   * Update Redux store with new status values
   */
  const updateReduxStore = useCallback(
    (status: any) => {
      const idStatus = status?.idVerification?.status;
      const incomeStatus = status?.incomeVerification?.status;

      if (idStatus) {
        dispatch(setIDVerification({ status: idStatus }));
      }

      if (incomeStatus) {
        dispatch(setIncomeVerification({ status: incomeStatus }));
      }
    },
    [dispatch],
  );

  /**
   * Start countdown timer
   */
  const startCountdown = useCallback(() => {
    clearCountdownTimer();

    let timeLeft = COUNTDOWN_SECONDS;
    setState((prev) => ({ ...prev, countdown: timeLeft }));

    countdownTimerRef.current = setInterval(() => {
      timeLeft -= 1;

      if (timeLeft <= 0) {
        clearCountdownTimer();
        setState((prev) => ({ ...prev, countdown: 0 }));
      } else {
        setState((prev) => ({ ...prev, countdown: timeLeft }));
      }
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearCountdownTimer]);

  /**
   * Show the "Check Status" button
   * Call this when polling completes without finding desired status
   */
  const showCheckButton = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showButton: true,
      countdown: 0,
      error: null,
    }));
  }, []);

  /**
   * Hide the "Check Status" button
   */
  const hideCheckButton = useCallback(() => {
    clearCountdownTimer();
    setState({
      showButton: false,
      countdown: 0,
      isChecking: false,
      error: null,
    });
  }, [clearCountdownTimer]);

  /**
   * Main function to check status when button is clicked
   */
  const checkStatus = useCallback(async () => {
    // Don't allow checking if countdown is active
    if (state.countdown > 0 || state.isChecking) {
      return;
    }

    setState((prev) => ({ ...prev, isChecking: true, error: null }));

    try {
      // Fetch the current verification status
      const status = await fetchVerificationStatus();

      // Update Redux store with new status
      updateReduxStore(status);
      checkNotifications(status);

      // Check if we've reached desired state
      if (hasReachedDesiredState(status)) {
        // Hide button if we found the desired status
        hideCheckButton();
      } else {
        // Start countdown for next check
        startCountdown();
        setState((prev) => ({ ...prev, isChecking: false }));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check status';
      setState((prev) => ({
        ...prev,
        isChecking: false,
        error: errorMessage,
      }));
    }
  }, [
    state.countdown,
    state.isChecking,
    fetchVerificationStatus,
    updateReduxStore,
    hasReachedDesiredState,
    hideCheckButton,
    startCountdown,
    checkNotifications,
  ]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearCountdownTimer();
    };
  }, [clearCountdownTimer]);

  return {
    // State
    showButton: state.showButton,
    countdown: state.countdown,
    isChecking: state.isChecking,
    isButtonDisabled: state.countdown > 0 || state.isChecking,
    error: state.error,

    // Actions
    checkStatus,
    showCheckButton,
    hideCheckButton,
  };
};
